import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type { SyntheticEvent } from 'react';
import { memo, useCallback } from 'react';
import type { IconBlockStateType, IconComponentType } from '@unifyapps/defs/blocks/Icon/types';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { clsx } from 'clsx';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { Tooltip } from '@unifyapps/ui/components/Tooltip';
import { Box } from '@unifyapps/ui/components/Box';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { getConditionalPropertyValue } from '@unifyapps/carbon/utils/conditionalValues';
import { Icon as BaseIcon } from '../common/Icon';

function Icon({
  blockState,
  attributesAndEventListeners,
  events,
  computedBlockState,
  dndRef,
}: BlockComponentProps<IconComponentType, IconBlockStateType, IconBlockStateType>) {
  const {
    content: { icon, addOns },
    appearance,
  } = computedBlockState;

  const { variant, size, styles, featured, transparent } = appearance;

  const { text: tooltipText, placement = 'top', width } = addOns?.tooltip ?? {};
  const { className: tooltipClassName, style: tooltipStyle } = useBlockAppearanceStyles({
    appearanceStyles: { width },
  });

  const iconName = getConditionalPropertyValue({ value: icon });
  const IconComponent = getIconFromRegistry(iconName);

  const { emitEvent } = useBlockEvents(events);
  const onClick = useCallback(
    (e: SyntheticEvent) => {
      void emitEvent({
        eventType: EventTypeEnum.OnClick,
        domEvent: e,
      });
    },
    [emitEvent],
  );

  const hasClickEvent = events?.some((event) => event.eventType === 'onClick');

  if (!IconComponent) {
    return null;
  }

  const iconEl = (
    <BaseIcon
      hasClickEvent={hasClickEvent}
      onClick={onClick}
      variant={variant}
      size={size}
      featured={featured}
      transparent={transparent}
      styles={styles}
      attributesAndEventListeners={attributesAndEventListeners}
      icon={icon}
    />
  );

  if (tooltipText) {
    const slotProps = {
      title: {
        className: clsx('p-xxs whitespace-pre-line', tooltipClassName),
        style: tooltipStyle,
      },
    };

    return (
      <Tooltip placement={placement} slotProps={slotProps} title={tooltipText}>
        {/*
        icon generated do not have forward ref, so we need to wrap it for div to render tooltip as
        tooltip ref is not attached to a dom element
        */}
        <Box className="h-fit w-fit" ref={dndRef} {...attributesAndEventListeners}>
          {iconEl}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box className="h-fit w-fit" ref={dndRef} {...attributesAndEventListeners}>
      {iconEl}
    </Box>
  );
}

export default memo(Icon);
